// @mui material components
import { Autocomplete, Switch, FormControlLabel } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useRef, useState, useEffect } from "react";

// PMS components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMaterialUIController } from "context";

import {
  assignSetting,
  getPlaceById,
  setSinglePlace,
  setSelectedPlace,
} from "store/slice/places/placeSlice";

const EXTENSION_LIMIT_OPTIONS = [
  ...Array.from({ length: 10 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  })),
  { label: "No Limit", value: null },
];

function Extension() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const placesData = useSelector((state) => state.places?.selectedPlace);

  const placeId =
    placesData?._id || new URLSearchParams(location.search).get("placeId");

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification((p) => ({ ...p, show: false })),
  });

  useEffect(() => {
    if (!placeId) return;

    dispatch(getPlaceById(placeId))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(setSinglePlace(res.place));
          formikRef.current?.setValues({
            isExtensionEnable: res.place?.isExtensionEnable ?? false,
            extensionExpireLimit: res.place?.extensionExpireLimit ?? "",
            maxExtensionLimit:
              res.place?.maxExtensionLimit === undefined
                ? null
                : res.place?.maxExtensionLimit,
          });
        }
      });
  }, [dispatch, placeId]);

  const handleSubmit = (values) => {
    const currentSetup = placesData?.setup || {};
    const currentSteps = currentSetup?.steps || {};
    const payload = {
      ...values,
      setup: {
        ...currentSetup,
        steps: {
          ...currentSteps,
          generalSettings: true,
        },
      },
      placeId
    };
    dispatch(assignSetting(payload))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          dispatch(getPlaceById(placeId))
            .unwrap()
            .then((r) => {
              if (r?.success) {
                dispatch(setSelectedPlace(r.place));
                dispatch(setSinglePlace(r.place));
              }
            });
        }

        setNotification({
          ...notification,
          color: success ? "success" : "error",
          icon: success ? "check" : "warning",
          title: success ? "Success" : "Error",
          content: res?.message,
          show: true,
        });
      })
      .catch((err) => {
        setNotification({
          ...notification,
          color: "error",
          icon: "warning",
          title: "Error",
          content: err?.message,
          show: true,
        });
      });
  };

  return (
    <MDBox>
      <MDBox pt={2} pb={3}>
        <Formik
          innerRef={formikRef}
          initialValues={{
            isExtensionEnable: false,
            extensionExpireLimit: "",
            maxExtensionLimit: null,
          }}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Extension Settings
                </MDTypography>
              </MDBox>

              <Grid container spacing={3}>
                {/* Enable Extension */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={props.values.isExtensionEnable}
                        onChange={(e) =>
                          props.setFieldValue(
                            "isExtensionEnable",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Enable Reservation Extension"
                  />
                </Grid>

                {/* Expire Limit */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={[15, 20, 25, 30, 35, 40, 45, 50, 55, 60]}
                    value={props.values.extensionExpireLimit}
                    onChange={(_, value) =>
                      props.setFieldValue("extensionExpireLimit", value)
                    }
                    disabled={!props.values.isExtensionEnable}
                    disableClearable
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Extension Expire Limit (Minutes)"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Max Extension Limit */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={EXTENSION_LIMIT_OPTIONS}
                    getOptionLabel={(o) => o.label}
                    value={
                      EXTENSION_LIMIT_OPTIONS.find(
                        (o) => o.value === props.values.maxExtensionLimit,
                      ) || null
                    }
                    onChange={(_, option) =>
                      props.setFieldValue(
                        "maxExtensionLimit",
                        option?.value ?? null,
                      )
                    }
                    disabled={!props.values.isExtensionEnable}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Max Extension Limit"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Save */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      color={sidenavColor}
                      variant="contained"
                      type="submit"
                    >
                      Save Settings
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </MDBox>

      <MDSnackbar
        {...notification}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
    </MDBox>
  );
}

export default Extension;
